/**
 * Types of notices from the message events.
 * It it generally preferable to specify different types of messages, rather than
 * having a `info` or `warning` notice. This is better than specifying `"kind"` of notices (`"info"`, `"warning"`, `"error"`, `"focused-notice"`)
 * as it gives the underlying implementation the choice to customize how it wants to show different types
 * of notices.
 *
 * With that in mind, there is no problem in specifying `"kind"` of a message. It depends on the implementation
 * on how they want to customize it. We should always give the underlying implementation the freedom to choose how
 * they want the UI of a notice to look. If you want to specify a `"kind"` of a notice, add a boolean/union property type
 * to denote the reason of the notice, so that it's still customizable for different types of implementation.
 *
 * > The major reason for this behavior is to make sure that the Pleased implementation is
 * > customizable for any client.
 *
 * @author Aakash More <moreakash77@hotmail.com>
 */
export const NoticeType = {
  /**
   * A general notice. This type of system notices will be shown in a generic way. Denoted to inform the user
   * of behavior of the messaging client.
   */
  GENERAL: 'notice.general',
  /**
   * A notice to represent that someone has joined the chat.
   */
  MEMBER_JOINED: 'notice.info.member.join',
  /**
   * A notice to represent that someone has left the chat.
   */
  MEMBER_LEFT: 'notice.info.member.left',
  /**
   * A notice to denote that the user is facing network issues and we do not guarantee the
   * delivery of a message or an event.
   */
  NETWORK_ISSUES: 'notice.warn.networkIssue',
  /**
   * Denotes a notice on the current queue position of a chat client.
   */
  QUEUE_POSITION: 'notice.info.queue-position',
  /**
   * Denotes if a chat has been closed.
   */
  CHAT_ENDED: 'notice.warn.chat-end',
  /**
   * Denotes if a voice chat has occurred with the current customer.
   */
  VOICE_CHAT_SESSION: 'notice.info.voice-chat',
  /**
   * Denotes if a live chat has occurred with the current customer.
   */
  LIVE_CHAT_SESSION: 'notice.info.live-chat',
  /**
   * Denotes if a customer has logged in between a chat session.
   */
  LOGGED_IN: 'notice.info.logged-in',
  /**
   * Denotes if a request was opened successfully.
   */
  OPEN_REQUEST_SUCCESSFUL: 'notice.success.request-opened',
  /**
   * Denotes the notice given as warning not to close the chat.
   */
  CHAT_WINDOW_DISCLAIMER: 'notice.warn.chat-window-disclaimer',
  /**
   * Denotes the follow up notice.
   */
  FOLLOW_UP_SUBMITTED: 'notice.warn.follow-up-help',
  /**
   * Denotes the follow up notice.
   */
  TICKET_CREATED: 'notice.warn.ticket-created',
  /**
   * A short lived notice denoting only the error.
   */
  ERROR: 'notice.error',
  /**
   * This notice should be shown only once. Can be used for messages like Queue position and agent typing. This
   * notice will hide once a new message/general notice is pushed to the message stack. If the new notice pushed is
   * of the same type, then the notice will be shown
   * @deprecated use `MessageListItem.isSticky` property instead. This is older implementation that does not allow for
   *             customization. This will not be included in newer versions of the library.
   */
  // STICKY: 'notice.sticky',
} as const;
